/* eslint-disable max-lines */

import type {
    JitsiAnticsEvent,
    JitsiAnticsMetricContext,
} from 'antics/types'
import React, {
    useContext, useEffect,
} from 'react'
import {
    buildJitsiAnticsMetricFields,
    buildJitsiRTCStatsMetricFields,
} from './utils/connectivity'
import {
    jitsiHangup, jitsiToggleChat,
    jitsiVideoConferenceJoined,
} from '../../actions/jitsi'

import {
    styledJitsi,
    styledReturnButton,
    styledVideoContainer,
} from './styles'
import {
    useAppDispatch,
    useAppSelector,
} from 'hooks'
import {
    useChannelDetails,
    useQueueAttributes,
} from 'hooks/ninchat'
import {EmbedContext} from 'components/EmbedContext'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import JitsiMeetExternalAPI from 'JitsiMeetExternalAPI'
import {JitsiState} from '../../reducers/jitsi'
import {discoverJitsi} from 'api/action/discoverJitsi'
import {getEnvironment} from '../../appconfig'
import {hasScreenSharingPermission} from 'utils/screensharing'

import {navigation} from 'navigation'
import {sendJitsiMetric} from 'api/action/sendJitsiMetric'
import {styled} from '../../stitches/config'
import {useTranslation} from 'react-i18next'

export type JitsiProps = {
    channel_id: string; // eslint-disable-line react/no-unused-prop-types
}

const RTCSTATS_POLL_INTERVAL_MS = 3_000

const getJitsiEndpointAddress = (): string => {
    const {
        apiHost,
        variant,
    } = getEnvironment()
    const tokens = apiHost.split('.')
    if (tokens.length > 1) {
        tokens.shift()
    }
    tokens.unshift(variant === 'stage' ? 'stage-jitsi-www' : 'jitsi-www')
    return tokens.join('.')
}

const StyledReturnButton = styled('button', styledReturnButton)
const StyledVideoContainer = styled('div', styledVideoContainer)
const StyledJitsi = styled('div', styledJitsi)

const JitsiView: React.FC<JitsiProps> = (JitsiProps): React.ReactElement => {
    const {channel_id} = JitsiProps
    const embed = useContext(EmbedContext)
    const isEmbedQueueChannel = embed?.variant === 'queue' || embed?.variant === 'channel'

    const dispatch = useAppDispatch()

    const {
        i18n,
        t,
    } = useTranslation()

    const {
        isChatOpen, jitsiToken, jitsiRoom,
    } = useAppSelector(({jitsi}: {jitsi: JitsiState}) => (
        {
            isChatOpen: jitsi.isChatOpen ?? false,
            jitsiRoom: jitsi.jitsi_room,
            jitsiToken: jitsi.jitsi_token,
        }
    ))

    const showChannelSidebar = useAppSelector(({ui}) => ui.show_channel_sidebar || false)
    const disableAutoGainControl = useAppSelector(({ninchat}) => ninchat.user_settings.disable_jitsi_auto_gain_control ?? false)

    const currentChannelId = history.state?.route?.name === 'Channel' ? history.state?.route?.params?.channel_id : undefined
    const currentChannel = useChannelDetails({channelId: channel_id})
    const queueAttributes = useQueueAttributes(currentChannel?.queueId)
    const currentUserIsModerator = useAppSelector(({ninchat}) => ninchat.user_channels[channel_id || '']?.channel_members?.[ninchat.user_id || '']?.member_attrs?.moderator)
    const currentUserIsOwner = useAppSelector(({ninchat}) => ninchat.user_channels[channel_id || '']?.channel_attrs?.owner_id === ninchat.user_id)
    const currentUserIsOperator = useAppSelector(({ninchat}) => ninchat.user_channels[channel_id || '']?.channel_members?.[ninchat.user_id || '']?.member_attrs?.operator)

    const canScreenShare = hasScreenSharingPermission({
        isAudienceChannel: currentChannel?.isAudienceChannel ?? false,
        isModerator: currentUserIsModerator ?? false,
        isOperator: currentUserIsOperator ?? false,
        isOwner: currentUserIsOwner ?? false,
        screen_sharing: queueAttributes?.screensharing,
    })

    const {
        isVideoActive, isVideoChannel, isClosed,
    } = useChannelDetails({channelId: channel_id})

    const defaultToolbarButtons = [
        'microphone',
        'camera',
        ...canScreenShare ? ['desktop'] : [],
        'tileview',
        'fullscreen',
        'hangup',
        'settings',
        'toggle-camera',
    ]

    const pipToolbarButtons = [
        'microphone',
        'camera',
        'hangup',
    ]

    const remoteVideoMenu = {
        disableGrantModerator: true,
        disableKick: true,
    }

    const JITSI_CONFIG_OVERWRITE = {
        analytics: {
            rtcstatsEnabled: false,
            rtcstatsExternalApiEnabled: true,
            rtcstatsPollInterval: RTCSTATS_POLL_INTERVAL_MS,
        },
        defaultLanguage: i18n.language,
        disableAGC: disableAutoGainControl,
        disableIncomingMessageSound: true,
        disableInviteFunctions: true,
        disableReactions: true,
        disableReactionsModeration: true,
        disableSelfView: false,
        disableSelfViewSettings: true,
        disableShortcuts: true,
        disableThirdPartyRequests: true,
        disabledNotifications: ['notify.moderator'],
        doNotStoreRoom: true,
        hideConferenceSubject: true,
        hideConferenceTimer: true,
        prejoinConfig: {
            enabled: true,
            hideDisplayName: true,
        },
        readOnlyName: true,
        remoteVideoMenu,
        speakerStats: {disabled: true},
        toolbarButtons: defaultToolbarButtons,
        ...isEmbedQueueChannel
            ? {
                customToolbarButtons: [
                    {
                        // eslint-disable-next-line max-len
                        icon: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTQuOTEzMDcgMi42NTgyM0M2Ljk4NzcgMi4zODg4OCA5LjEwMjk2IDIuMjUgMTEuMjUwMyAyLjI1QzEzLjM5NzQgMi4yNSAxNS41MTI0IDIuMzg4ODUgMTcuNTg2OSAyLjY1ODE1QzE5LjUwOTEgMi45MDc2OSAyMC44NzgzIDQuNTE5MzcgMjAuOTkyMyA2LjM4NDk1QzIwLjY2NjUgNi4yNzYxNCAyMC4zMjEyIDYuMjAzOTYgMTkuOTYgNi4xNzM5OUMxOC41NzE1IDYuMDU4NzQgMTcuMTY3MyA2IDE1Ljc1IDZDMTQuMzMyNiA2IDEyLjkyODUgNi4wNTg3NCAxMS41NCA2LjE3Mzk4QzkuMTgxNyA2LjM2OTcxIDcuNSA4LjM2NDY3IDcuNSAxMC42MDgyVjE0Ljg5MzdDNy41IDE2LjU4NDQgOC40NTQ2OCAxOC4xMzI2IDkuOTMyOCAxOC44Nzc5TDcuMjgwMzMgMjEuNTMwM0M3LjA2NTgzIDIxLjc0NDggNi43NDMyNCAyMS44MDkgNi40NjI5OSAyMS42OTI5QzYuMTgyNzMgMjEuNTc2OCA2IDIxLjMwMzMgNiAyMVYxNi45NzA1QzUuNjM2NDkgMTYuOTMxNiA1LjI3NDE3IDE2Ljg4ODcgNC45MTMwOCAxNi44NDE4QzIuOTA0NjYgMTYuNTgxIDEuNSAxNC44MzMzIDEuNSAxMi44NjI2VjYuNjM3MzhDMS41IDQuNjY2NzIgMi45MDQ2NiAyLjkxODk5IDQuOTEzMDcgMi42NTgyM1oiIGZpbGw9IndoaXRlIi8+CjxwYXRoIGQ9Ik0xNS43NSA3LjVDMTQuMzc0MSA3LjUgMTMuMDExNCA3LjU1NzAyIDExLjY2NDEgNy42Njg4NEMxMC4xMjQ4IDcuNzk2NiA5IDkuMTAyODIgOSAxMC42MDgyVjE0Ljg5MzdDOSAxNi40MDE0IDEwLjEyOCAxNy43MDgzIDExLjY2OTIgMTcuODM0MUMxMi45MTMxIDE3LjkzNTcgMTQuMTcgMTcuOTkxMiAxNS40Mzg0IDE3Ljk5OUwxOC4yMTk3IDIwLjc4MDNDMTguNDM0MiAyMC45OTQ4IDE4Ljc1NjggMjEuMDU5IDE5LjAzNyAyMC45NDI5QzE5LjMxNzMgMjAuODI2OCAxOS41IDIwLjU1MzMgMTkuNSAyMC4yNVYxNy44NjAxQzE5LjYxMDMgMTcuODUxOCAxOS43MjA2IDE3Ljg0MzIgMTkuODMwNyAxNy44MzQyQzIxLjM3MiAxNy43MDg1IDIyLjUgMTYuNDAxNSAyMi41IDE0Ljg5MzhWMTAuNjA4MkMyMi41IDkuMTAyODMgMjEuMzc1MiA3Ljc5NjYxIDE5LjgzNiA3LjY2ODg1QzE4LjQ4ODYgNy41NTcwMiAxNy4xMjU5IDcuNSAxNS43NSA3LjVaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4=',
                        id: 'show-chat',
                        text: 'Show chat',
                    },
                ],
            }
            : {},
        toolbarConfig: {alwaysVisible: false},
    }

    const JITSI_INTERFACE_CONFIG_OVERWRITE = {
        DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
        HIDE_KICK_BUTTON_FOR_GUESTS: true,
        LANG_DETECTION: false,
        MEETING_NAME_ENABLED: false,
        MOBILE_APP_PROMO: false,
        SETTINGS_SECTIONS: [
            'devices',
        ],
        SHOW_CHROME_EXTENSION_BANNER: false,
        SHOW_JITSI_WATERMARK: false,
        SHOW_PROMOTIONAL_CLOSE_PAGE: false,
        SHOW_WATERMARK_FOR_GUESTS: false,
    }

    const containerRef = React.createRef<HTMLDivElement>()

    const jitsiMeetAPI = React.useRef<JitsiMeetExternalAPI>()
    const jitsiStartTime = React.useRef<number | null>(null)

    const onClose = () => {
        jitsiMeetAPI.current?.dispose()
        dispatch(jitsiHangup())
        jitsiStartTime.current = null
    }

    const startConference = () => {
        let metricContext: JitsiAnticsMetricContext = {
            c: channel_id,
            j: jitsiRoom ?? undefined,
            r: currentChannel?.realmId,
        }

        try {
            const domain = getJitsiEndpointAddress()
            metricContext = {
                ...metricContext,
                d: domain,
            }
            // Measures user-perceived join duration from Jitsi API creation to videoConferenceJoined.
            jitsiStartTime.current = Date.now()

            const trackJitsiEvent = (eventName: JitsiAnticsEvent, event?: unknown) => {
                dispatch(sendJitsiMetric({
                    context: metricContext,
                    event: eventName,
                    fields: buildJitsiAnticsMetricFields({
                        event,
                        eventName,
                        room: jitsiRoom,
                        startedAt: jitsiStartTime.current,
                    }),
                }))
            }
            const endpointRole = isEmbedQueueChannel ? 'customer' : 'agent'
            const trackRTCStatsEntry = (value: unknown) => {
                const fields = buildJitsiRTCStatsMetricFields(value, endpointRole)

                if (!fields) {
                    return
                }

                dispatch(sendJitsiMetric({
                    context: metricContext,
                    event: 'rtcStats',
                    fields,
                }))
            }

            const options = {
                configOverwrite: JITSI_CONFIG_OVERWRITE,
                height: '100%',
                interfaceConfigOverwrite: JITSI_INTERFACE_CONFIG_OVERWRITE,
                jwt: jitsiToken,
                parentNode: containerRef.current,
                roomName: jitsiRoom,
                width: '100%',
            }
            jitsiMeetAPI.current = new JitsiMeetExternalAPI(domain, options)

            jitsiMeetAPI.current.addEventListener(
                'browserSupport',
                (event: unknown) => trackJitsiEvent('browserSupport', event),
            )
            jitsiMeetAPI.current.addEventListener(
                'cameraError',
                (event: unknown) => trackJitsiEvent('cameraError', event),
            )
            jitsiMeetAPI.current.addEventListener(
                'conferenceCreatedTimestamp',
                (event: unknown) => trackJitsiEvent('conferenceCreatedTimestamp', event),
            )
            jitsiMeetAPI.current.addEventListener(
                'deviceListChanged',
                (event: unknown) => trackJitsiEvent('deviceListChanged', event),
            )
            jitsiMeetAPI.current.addEventListener(
                'errorOccurred',
                (event: unknown) => trackJitsiEvent('errorOccurred', event),
            )
            jitsiMeetAPI.current.addEventListener(
                'micError',
                (event: unknown) => trackJitsiEvent('micError', event),
            )
            jitsiMeetAPI.current.addEventListener(
                'p2pStatusChanged',
                (event: unknown) => trackJitsiEvent('p2pStatusChanged', event),
            )
            jitsiMeetAPI.current.addEventListener(
                'participantJoined',
                (event: unknown) => trackJitsiEvent('participantJoined', event),
            )
            jitsiMeetAPI.current.addEventListener(
                'participantLeft',
                (event: unknown) => trackJitsiEvent('participantLeft', event),
            )
            jitsiMeetAPI.current.addEventListener(
                'participantMuted',
                (event: unknown) => trackJitsiEvent('participantMuted', event),
            )
            jitsiMeetAPI.current.addEventListener(
                'peerConnectionFailure',
                (event: unknown) => trackJitsiEvent('peerConnectionFailure', event),
            )
            jitsiMeetAPI.current.addEventListener(
                'rtcStatsEntry',
                trackRTCStatsEntry,
            )

            jitsiMeetAPI.current.addEventListener('readyToClose', (event: unknown) => {
                trackJitsiEvent('readyToClose', event)
                onClose()
            })
            jitsiMeetAPI.current.addEventListener('toolbarButtonClicked', (event: {key?: string}) => {
                if (event.key === 'show-chat') {
                    dispatch(jitsiToggleChat())
                }
            })

            /*
            jitsiMeetAPI.current.addEventListener(
                'participantRoleChanged',
                (event: unknown) => trackJitsiEvent('participantRoleChanged', event),
            )

            jitsiMeetAPI.current.addEventListener(
                'audioAvailabilityChanged',
                (event: unknown) => trackJitsiEvent('audioAvailabilityChanged', event),
            )

            jitsiMeetAPI.current.addEventListener(
                'videoAvailabilityChanged',
                (event: unknown) => trackJitsiEvent('videoAvailabilityChanged', event),
            )
            */
            jitsiMeetAPI.current.addEventListener(
                'videoConferenceJoined',
                (event: {roomName?: string, visitor?: boolean}) => {
                    trackJitsiEvent('videoConferenceJoined', event)
                    dispatch(jitsiVideoConferenceJoined(true))
                },
            )

            jitsiMeetAPI.current.addEventListener(
                'videoConferenceLeft',
                (event: unknown) => {
                    trackJitsiEvent('videoConferenceLeft', event)
                    dispatch(jitsiVideoConferenceJoined(false))
                },
            )
            jitsiMeetAPI.current.addEventListener(
                'videoQualityChanged',
                (event: unknown) => trackJitsiEvent('videoQualityChanged', event),
            )
        } catch (error) {
            dispatch(sendJitsiMetric({
                context: metricContext,
                event: 'errorOccurred',
                fields: {
                    a: 'FRONTEND',
                    f: true,
                    fail: true,
                    m: error instanceof Error ? error.message : String(error),
                    n: 'JitsiMeetExternalAPI',
                },
            }))
            console.error('Failed to load Jitsi API', error)
        }
    }


    useEffect(() => {
        dispatch(discoverJitsi({channel_id}))
    }, [channel_id])

    useEffect(() => {
        // Always display toolbar when not on the currently active video channel
        if (jitsiMeetAPI.current) {
            jitsiMeetAPI.current.executeCommand('overwriteConfig', {
                toolbarButtons: channel_id === currentChannelId ? defaultToolbarButtons : pipToolbarButtons,
                toolbarConfig: {alwaysVisible: channel_id !== currentChannelId},
            })
        }
    }, [currentChannelId])

    useEffect(() => {
        if (jitsiRoom && jitsiToken) {
            // if jitsiMeetAPI.current is not undefined, it means that the jitsi room has changed
            // so we need to dispose the current jitsiMeetAPI.current and start a new conference
            if (jitsiMeetAPI.current) {
                jitsiMeetAPI.current?.dispose()
                jitsiMeetAPI.current = undefined
            }
            startConference()
        }
    }, [jitsiRoom, jitsiToken])

    useEffect(() => {
        // is video is active for this channel but the channel is not a video channel
        // happen when user switch from video channel to non-video channel
        if (isVideoActive && !isVideoChannel) {
            dispatch(jitsiHangup())
        }
    }, [isVideoChannel])

    useEffect(() => {
        // is video is active for this channel but the channel is closed
        // happen when user close the channel from settings
        if (isVideoActive && isClosed) {
            dispatch(jitsiHangup())
        }
    }, [isClosed])

    const handleReturnToChannel = () => {
        if (isEmbedQueueChannel) {
            dispatch(jitsiHangup())
        } else {
            navigation.push('Channel', {channel_id})
        }
    }

    return (
        <StyledJitsi
            channelSidebarOpen={showChannelSidebar}
            chatOpen={isChatOpen}
            floating={{
                '@initial': false,
                '@small': true,
            }}
            isEmbedQueueChannel={isEmbedQueueChannel}
            pip={channel_id !== currentChannelId}
        >
            <StyledVideoContainer data-id='jitsi-iframe-wrapper' isEmbedQueueChannel={isEmbedQueueChannel} pip={channel_id !== currentChannelId} ref={containerRef} />
            {isEmbedQueueChannel ?
                null :
                <StyledReturnButton
                    onClick={handleReturnToChannel}
                    pip={channel_id !== currentChannelId}
                    title={t('Retun to video channel')}
                />}
        </StyledJitsi>
    )
}

export default JitsiView
